import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getBanners } from "../../redux/actions/bannerActions";
import { DashboardLayout } from "../dashboard/DashboardLayout";
import { FullScreenLoader } from "../Components/Loader";
import { FaImage } from "react-icons/fa";
import dayjs from "dayjs";

const CustomerOffersView = () => {
  const dispatch = useDispatch();
  const { loading, banners, error, count } = useSelector((state) => state.banner);
  const { userInfo } = useSelector((state) => state.auth);

  useEffect(() => {
    if (userInfo?.access_token) {
      // For customer, fetch banners filtered by role_id=4 and is_live=true
      const filters = {
        role_id: 4, // Customer role
        is_live: true, // Only show live banners
      };
      dispatch(getBanners(filters, userInfo.access_token));
    }
  }, [dispatch, userInfo?.access_token, userInfo?.role_id]);

  if (error && !banners.length) {
    return (
      <DashboardLayout>
        <div className="p-4">
          <p className="text-red-600">Error: {error}</p>
        </div>
      </DashboardLayout>
    );
  }

  const bannerArray = Array.isArray(banners) ? banners : [];

  // Filter only live banners
  const now = dayjs();
  const filteredBanners = bannerArray.filter((banner) => {
    if (banner.is_live !== true) return false;
    
    if (banner.role_ids && banner.role_ids.length > 0) {
      const hasCustomerRole = banner.role_ids.includes(4) || banner.role_ids.includes("4");
      if (!hasCustomerRole) return false;
    }
    
    if (banner.start_date) {
      const startDate = dayjs(banner.start_date);
      if (now.isBefore(startDate)) return false;
    }
    
    if (banner.end_date) {
      const endDate = dayjs(banner.end_date);
      if (now.isAfter(endDate)) return false;
    }
    
    return true;
  });

  return (
    <DashboardLayout>
      <FullScreenLoader 
        show={loading} 
        message="Loading offers..." 
        variant="primary"
        blurIntensity="md"
      />
      <div className="p-4 sm:p-6 lg:p-8 bg-gradient-to-br from-gray-50 via-white to-amber-50 min-h-screen">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-6 sm:mb-8">
            <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-800 mb-2">
              Special Offers
            </h1>
            <p className="text-gray-600 text-sm sm:text-base">
              Explore our latest promotional offers and deals
            </p>
          </div>

          {/* Loading State */}
          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-amber-600"></div>
              <p className="mt-2 text-gray-600">Loading offers...</p>
            </div>
          ) : filteredBanners.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-lg shadow-sm">
              <FaImage className="mx-auto text-gray-400 text-5xl mb-4" />
              <p className="text-gray-600 text-lg font-semibold">No offers available</p>
              <p className="text-gray-500 text-sm mt-2">
                Check back later for exciting offers!
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6">
              {filteredBanners.map((banner) => (
                <div
                  key={banner.id}
                  className="group bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden transform hover:scale-105 border-2 border-transparent hover:border-amber-300"
                >
                  {/* Banner Image */}
                  <div className="relative h-48 sm:h-56 md:h-64 bg-gradient-to-br from-amber-100 to-orange-100 overflow-hidden">
                    {banner.url_link ? (
                      <img
                        src={banner.url_link}
                        alt={banner.banner_type || "Offer"}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                        onError={(e) => {
                          e.target.src = "https://via.placeholder.com/400x300?text=Offer+Image";
                        }}
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-amber-100 to-orange-100">
                        <FaImage className="text-gray-400 text-5xl" />
                      </div>
                    )}
                    {/* Live Badge */}
                    {banner.is_live && (
                      <div className="absolute top-3 right-3">
                        <span className="px-3 py-1 bg-green-500 text-white text-xs font-bold rounded-full shadow-lg animate-pulse">
                          LIVE
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Banner Details */}
                  <div className="p-4 sm:p-5">
                    <h3 className="text-lg sm:text-xl font-bold text-gray-800 mb-2 line-clamp-2 group-hover:text-amber-600 transition-colors">
                      {banner.banner_type || "Special Offer"}
                    </h3>
                    {banner.client_name && (
                      <p className="text-sm text-gray-600 mb-3">
                        <span className="font-medium">By:</span> {banner.client_name}
                      </p>
                    )}
                    
                    {/* View Button */}
                    <button
                      onClick={() => window.open(banner.url_link, "_blank")}
                      className="w-full mt-4 bg-gradient-to-r from-amber-500 to-amber-600 text-white py-2.5 px-4 rounded-lg font-semibold hover:from-amber-600 hover:to-amber-700 transition-all duration-200 shadow-md hover:shadow-lg transform hover:scale-105"
                    >
                      View Offer
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Results Count */}
          {!loading && filteredBanners.length > 0 && (
            <div className="mt-6 text-center text-gray-600 text-sm">
              Showing {filteredBanners.length} offer{filteredBanners.length !== 1 ? "s" : ""}
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default CustomerOffersView;

